import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/shared/cn';

type ScrollCueProps = {
  children: ReactNode;
  className?: string;
  /** When set, caps scroll height (e.g. `18rem`, `280px`). */
  maxHeight?: string;
  /** Label prefix for the overflow count — default "more". */
  moreLabel?: string;
};

const LINE_HEIGHT_PX = 24;

function countHiddenRows(scroller: HTMLElement): number {
  const rowSelector = 'tbody tr, li, [data-scroll-cue-row]';
  const rows = scroller.querySelectorAll(rowSelector);
  if (rows.length === 0) return 0;
  const scrollerBottom = scroller.getBoundingClientRect().bottom;
  let hidden = 0;
  rows.forEach((row) => {
    const rect = row.getBoundingClientRect();
    if (rect.height < 1) return;
    if (rect.bottom > scrollerBottom + 2) hidden += 1;
  });
  return hidden;
}

function sampleRowHeight(scroller: HTMLElement): number {
  const rows = scroller.querySelectorAll('tbody tr, li, [data-scroll-cue-row]');
  for (const row of rows) {
    const height = row.getBoundingClientRect().height;
    if (height >= 1) return height;
  }
  return 0;
}

function hiddenLineEstimate(scroller: HTMLElement): number {
  const remaining =
    scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
  return Math.max(1, Math.ceil(remaining / LINE_HEIGHT_PX));
}

/**
 * Bottom-edge fade + "N more" when capped content overflows.
 * Row count matches visible children whose bottom sits below the scroller viewport.
 */
export function ScrollCue({ children, className, maxHeight, moreLabel = 'more' }: ScrollCueProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [hiddenRows, setHiddenRows] = useState(0);
  const [showCue, setShowCue] = useState(false);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const measure = () => {
      const rowHeight = sampleRowHeight(el);
      const overflowPx = el.scrollHeight - el.clientHeight;
      const hiddenPx = overflowPx - el.scrollTop;
      const hasHiddenContent =
        rowHeight > 0 ? hiddenPx >= rowHeight - 1 : hiddenPx >= LINE_HEIGHT_PX;
      const below = hasHiddenContent && el.scrollTop + el.clientHeight < el.scrollHeight - 2;
      setShowCue(below);
      if (!below) {
        setHiddenRows(0);
        return;
      }
      const rowChildren = el.querySelectorAll('tbody tr, li, [data-scroll-cue-row]');
      const rowCount = countHiddenRows(el);
      if (rowChildren.length > 0) {
        if (rowCount === 0) {
          setShowCue(false);
          setHiddenRows(0);
          return;
        }
        setHiddenRows(rowCount);
      } else {
        setHiddenRows(hiddenLineEstimate(el));
      }
    };

    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    for (const row of el.querySelectorAll('tbody tr, li, [data-scroll-cue-row]')) {
      ro.observe(row);
    }
    return () => {
      el.removeEventListener('scroll', measure);
      ro.disconnect();
    };
  }, [children, maxHeight]);

  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      <div
        ref={scrollerRef}
        className={cn(
          'scrollbar-subtle min-h-0 overflow-y-auto',
          maxHeight ? undefined : 'max-h-full',
        )}
        style={maxHeight ? { maxHeight } : undefined}
      >
        {children}
      </div>
      {showCue && hiddenRows > 0 ? (
        <div
          className="relative flex-shrink-0 border-t border-[var(--hairline)] px-4 py-1.5"
          aria-hidden={hiddenRows === 0}
        >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-full h-6 bg-gradient-to-t from-[var(--scroll-cue-fade)] to-transparent"
            aria-hidden
          />
          <p className="text-center text-[10px] font-medium text-[var(--ink-secondary)]">
            {hiddenRows} {moreLabel}
          </p>
        </div>
      ) : null}
    </div>
  );
}
